<template>
  <div class="limited-student-dashboard">
    <v-container :class="$vuetify.display.mobile ? 'py-1 px-1' : 'py-6'" fluid>
      <!-- Welcome Section - Hide on Mobile -->
      <v-row v-if="!$vuetify.display.mobile" class="mb-4">
        <v-col cols="12">
          <v-card class="welcome-card pa-4" elevation="6">
            <v-row align="center">
              <v-col cols="12" md="8">
                <h1 class="text-h4 mb-2 text-white">
                  {{ $t('limitedStudent.dashboard.welcome') }}, {{ user?.fio || user?.firstName }}!
                </h1>
                <p class="text-h6 text-white opacity-90">
                  {{ $t('limitedStudent.dashboard.subtitle') }}
                </p>
              </v-col>
              <v-col cols="12" md="4" class="text-center">
                <v-avatar size="100" class="profile-avatar">
                  <v-icon size="50" color="white">mdi-account-circle</v-icon>
                </v-avatar>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>

      <!-- Telegram Connect -->
      <v-row class="mb-4">
        <v-col cols="12">
          <v-alert
            color="info"
            variant="tonal"
            border="start"
            class="telegram-connect-alert"
          >
            <template #prepend>
              <v-icon icon="mdi-telegram" />
            </template>
            <div class="telegram-connect-content">
              <div>
                <div class="text-subtitle-1 font-weight-bold">Telegram хабарламалары</div>
                <div class="text-body-2 text-medium-emphasis">
                  Вождение уақыты мен еске салуларды Telegram арқылы алыңыз.
                </div>
              </div>
              <v-btn
                color="info"
                variant="elevated"
                :block="$vuetify.display.xs"
                :loading="telegramLinkLoading"
                @click="openTelegramLink"
              >
                <v-icon start>mdi-telegram</v-icon>
                Telegram-ға жазылу
              </v-btn>
            </div>
            <div v-if="telegramManualCommand" class="telegram-code-fallback">
              <div class="text-caption text-medium-emphasis">
                {{ $t('student.dashboard.telegramFallback') }}
              </div>
              <div class="telegram-code-row">
                <code>{{ telegramManualCommand }}</code>
                <v-btn
                  icon="mdi-content-copy"
                  size="x-small"
                  variant="text"
                  color="info"
                  :aria-label="$t('student.dashboard.telegramCopyCode')"
                  @click="copyTelegramCode"
                />
              </div>
            </div>
          </v-alert>
        </v-col>
      </v-row>

      <!-- Status Alert -->
      <v-row v-if="showStatusAlert" class="mb-6">
        <v-col cols="12">
          <v-alert
            v-model="showStatusAlert"
            color="warning"
            variant="tonal"
            border="start"
            class="status-alert"
            closable
          >
            <template #title>
              <v-icon icon="mdi-information" class="me-2" />
              {{ $t('limitedStudent.dashboard.statusTitle') }}
            </template>
            <p class="mb-2">{{ $t('limitedStudent.dashboard.statusMessage') }}</p>
            <v-btn
              color="warning"
              variant="outlined"
              size="small"
              @click="contactAdmin"
            >
              <v-icon start>mdi-phone</v-icon>
              {{ $t('limitedStudent.dashboard.contactAdmin') }}
            </v-btn>
          </v-alert>
        </v-col>
      </v-row>

      <v-row>
        <!-- Left Column - Main Info -->
        <v-col cols="12" lg="8">
          <!-- My Instructor Card -->
          <v-card class="mb-6" elevation="4">
            <v-card-title class="d-flex align-center">
              <v-icon icon="mdi-account-tie" color="success" class="me-2" />
              {{ $t('limitedStudent.dashboard.myInstructor') }}
            </v-card-title>
            <v-divider />
            <v-card-text>
              <div v-if="instructor" class="instructor-info">
                <div class="d-flex align-center mb-4">
                  <v-avatar size="64" class="me-4" color="success">
                    <v-img
                      v-if="instructor.photo"
                      :src="getInstructorPhoto(instructor)"
                      :alt="instructor.fio"
                    />
                    <v-icon v-else size="32">mdi-account</v-icon>
                  </v-avatar>
                  <div class="flex-grow-1">
                    <h3 class="text-h6 font-weight-bold text-success">{{ instructor.fio }}</h3>
                    <p class="text-body-2 text-medium-emphasis mb-1">
                      <v-icon icon="mdi-star" size="16" color="yellow-darken-2" class="me-1" />
                      {{ instructor.rating || 'Нет рейтинга' }} 
                      <span v-if="instructor.totalRatings">({{ instructor.totalRatings }} {{ $t('common.reviews') }})</span>
                    </p>
                    <p class="text-body-2 text-medium-emphasis">
                      <v-icon icon="mdi-calendar-clock" size="16" class="me-1" />
                      {{ $t('limitedStudent.dashboard.experience') }}: {{ instructor.experience || 'Не указан' }}
                    </p>
                  </div>
                </div>
                
                <!-- Contact Instructor -->
                <div class="contact-actions">
                  <v-btn
                    v-if="features.chat"
                    color="success"
                    variant="elevated"
                    @click="openChat"
                  >
                    <v-icon start>mdi-chat</v-icon>
                    {{ $t('limitedStudent.dashboard.openChat') }}
                  </v-btn>
                </div>
              </div>
              <div v-else class="text-center py-8">
                <v-icon icon="mdi-account-off" size="64" color="grey" class="mb-4" />
                <p class="text-body-1 text-medium-emphasis">
                  {{ $t('limitedStudent.dashboard.noInstructor') }}
                </p>
                <v-btn
                  color="primary"
                  variant="elevated"
                  @click="$router.push('/instructors')"
                  class="mt-2"
                >
                  <v-icon start>mdi-account-search</v-icon>
                  {{ $t('limitedStudent.dashboard.findInstructor') }}
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Right Column - Schedule & Quick Actions -->
        <v-col cols="12" lg="4">
          <v-card class="mb-6" elevation="4">
            <v-card-title>
              <v-icon icon="mdi-telegram" color="info" class="me-2" />
              Telegram
            </v-card-title>
            <v-divider />
            <v-card-text>
              <p class="text-body-2 text-medium-emphasis mb-4">
                Вождение уақыты мен еске салуларды Telegram арқылы алыңыз.
              </p>
              <v-btn
                color="info"
                variant="elevated"
                block
                :loading="telegramLinkLoading"
                @click="openTelegramLink"
              >
                <v-icon start>mdi-telegram</v-icon>
                Telegram-ға жазылу
              </v-btn>
            </v-card-text>
          </v-card>

          <!-- Next Lesson Card -->
          <v-card class="mb-6" elevation="4">
            <v-card-title class="d-flex align-center">
              <v-icon icon="mdi-calendar-clock" color="primary" class="me-2" />
              {{ $t('limitedStudent.dashboard.nextLesson') }}
            </v-card-title>
            <v-divider />
            <v-card-text>
              <div v-if="nextLesson" class="next-lesson-info">
                <!-- Countdown Timer -->
                <div class="countdown-section text-center mb-4">
                  <div class="countdown-display pa-4 rounded-lg" :class="countdownClass">
                    <h2 class="text-h4 font-weight-bold mb-1 text-primary">{{ timeUntilLesson.display }}</h2>
                    <p class="text-body-2 text-medium-emphasis">{{ timeUntilLesson.label }}</p>
                  </div>
                </div>

                <!-- Lesson Details -->
                <div class="lesson-details">
                  <div class="detail-row mb-3">
                    <v-icon icon="mdi-calendar" color="primary" class="me-2" />
                    <span class="text-subtitle-2">{{ formatLessonDate(nextLesson.date) }}</span>
                  </div>
                  <div class="detail-row mb-3">
                    <v-icon icon="mdi-clock" color="primary" class="me-2" />
                    <span class="text-subtitle-2">{{ nextLesson.timeSlot }}</span>
                  </div>
                  <div class="detail-row mb-3">
                    <v-icon icon="mdi-map-marker" color="primary" class="me-2" />
                    <span class="text-subtitle-2">{{ nextLesson.location || $t('limitedStudent.dashboard.locationTBD') }}</span>
                  </div>
                </div>
              </div>
              <div v-else class="no-lesson text-center py-6">
                <v-icon icon="mdi-calendar-remove" size="48" color="grey" class="mb-3" />
                <p class="text-body-1 text-medium-emphasis mb-3">
                  {{ $t('limitedStudent.dashboard.noScheduledLessons') }}
                </p>
                <v-btn
                  color="primary"
                  variant="outlined"
                  size="small"
                  @click="requestLesson"
                >
                  <v-icon start>mdi-calendar-plus</v-icon>
                  {{ $t('limitedStudent.dashboard.requestLesson') }}
                </v-btn>
              </div>
            </v-card-text>
          </v-card>

          <!-- Quick Actions -->
          <v-card class="mb-6" elevation="4">
            <v-card-title>
              <v-icon icon="mdi-lightning-bolt" color="warning" class="me-2" />
              {{ $t('limitedStudent.dashboard.quickActions') }}
            </v-card-title>
            <v-divider />
            <v-card-text class="pa-2">
              <v-list density="compact">
                <v-list-item
                  v-for="action in quickActions"
                  :key="action.key"
                  @click="action.action"
                  class="quick-action-item"
                  :prepend-icon="action.icon"
                >
                  <v-list-item-title>{{ $t(`limitedStudent.actions.${action.key}`) }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>

          <!-- Learning Progress -->
          <v-card class="elevation-4">
            <v-card-title>
              <v-icon icon="mdi-chart-line" color="success" class="me-2" />
              {{ $t('limitedStudent.dashboard.learningProgress') }}
            </v-card-title>
            <v-divider />
            <v-card-text>
              <div class="progress-stats">
                <div class="stat-item mb-4">
                  <div class="d-flex justify-space-between align-center mb-2">
                    <span class="text-body-2">{{ $t('limitedStudent.dashboard.totalLessons') }}</span>
                    <span class="text-h6 font-weight-bold text-primary">{{ learningStats.totalLessons }}</span>
                  </div>
                </div>
                <div class="stat-item mb-4">
                  <div class="d-flex justify-space-between align-center mb-2">
                    <span class="text-body-2">{{ $t('limitedStudent.dashboard.completedHours') }}</span>
                    <span class="text-h6 font-weight-bold text-success">{{ learningStats.completedHours }}h</span>
                  </div>
                </div>
                <div class="stat-item">
                  <div class="d-flex justify-space-between align-center mb-2">
                    <span class="text-body-2">{{ $t('limitedStudent.dashboard.nextMilestone') }}</span>
                    <span class="text-body-2 text-warning font-weight-bold">{{ learningStats.nextMilestone }}</span>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Lesson Request Modal -->
    <v-dialog 
    v-model="showLessonRequestModal"
    max-width="600"
    persistent
  >
    <v-card>
      <v-card-title class="text-h6 font-weight-bold">
        {{ $t('limitedStudent.lessonRequest.title') }}
      </v-card-title>
      
      <v-card-text>
        <v-form @submit.prevent="submitLessonRequest">
          <div class="mb-4">
            <div class="text-body-2 text-medium-emphasis mb-2">
              {{ $t('limitedStudent.lessonRequest.instructorLabel') }}: 
              <strong>{{ instructor?.fio }}</strong>
            </div>
          </div>

          <!-- Instructor Availability Section -->
          <v-card variant="outlined" class="mb-4" v-if="instructor">
            <v-card-title class="text-body-1 font-weight-bold py-3">
              {{ $t('limitedStudent.lessonRequest.availability') }}
            </v-card-title>
            <v-card-text class="py-2">
              <div class="mb-3">
                <div class="text-caption text-medium-emphasis mb-1">
                  {{ $t('limitedStudent.lessonRequest.workingDays') }}:
                </div>
                <div class="d-flex flex-wrap gap-1">
                  <v-chip 
                    v-for="day in getInstructorWorkingDays()" 
                    :key="day"
                    size="small"
                    color="primary"
                    variant="outlined"
                  >
                    {{ day }}
                  </v-chip>
                </div>
              </div>
              <div>
                <div class="text-caption text-medium-emphasis mb-1">
                  {{ $t('limitedStudent.lessonRequest.availableTime') }}:
                </div>
                <div class="d-flex flex-wrap gap-1">
                  <v-chip 
                    v-for="time in getInstructorWorkingTimes()" 
                    :key="time"
                    size="small"
                    color="success"
                    variant="outlined"
                  >
                    {{ time }}
                  </v-chip>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <v-textarea
            v-model="lessonRequestForm.message"
            :label="$t('limitedStudent.lessonRequest.messageLabel')"
            :placeholder="$t('limitedStudent.lessonRequest.messagePlaceholder')"
            rows="4"
            variant="outlined"
            class="mb-4"
            :rules="[v => !!v || $t('limitedStudent.lessonRequest.messageRequired')]"
          />

          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="lessonRequestForm.preferredDate"
                :label="$t('limitedStudent.lessonRequest.preferredDate')"
                type="date"
                variant="outlined"
                class="mb-2"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="lessonRequestForm.preferredTime"
                :label="$t('limitedStudent.lessonRequest.preferredTime')"
                type="time"
                variant="outlined"
                class="mb-2"
              />
            </v-col>
          </v-row>

          <div class="text-caption text-medium-emphasis">
            {{ $t('limitedStudent.lessonRequest.note') }}
          </div>
        </v-form>
      </v-card-text>

      <v-card-actions class="px-6 pb-6">
        <v-spacer />
        <v-btn
          variant="text"
          @click="showLessonRequestModal = false"
          :disabled="requestSubmitting"
        >
          {{ $t('common.cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          variant="elevated"
          @click="submitLessonRequest"
          :loading="requestSubmitting"
          :disabled="!lessonRequestForm.message.trim()"
        >
          {{ $t('limitedStudent.lessonRequest.sendRequest') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { useChatStore } from '@/stores/chat'
import { useSnackbarStore } from '@/stores/snackbar'
import api from '@/utils/api'
import { features } from '@/config/features'

// Helper function for instructor photos
const getInstructorPhoto = (instructor) => {
  if (!instructor?.photo) return null
  // If photo already starts with /api/images/, return as is
  if (instructor.photo.startsWith('/api/images/')) return instructor.photo
  
  // Clean the path - remove leading slashes and 'instructors/' prefix if present
  const cleanPath = instructor.photo.replace(/^\/+/, '').replace(/^instructors\//, '')
  
  // Construct proper API URL
  return `/api/images/instructors/${cleanPath}`
}

const { t } = useI18n()
const authStore = useAuthStore()
const chatStore = useChatStore()
const snackbarStore = useSnackbarStore()

// Reactive data
const instructor = ref(null)
const nextLesson = ref(null)
const loading = ref(false)
const telegramLinkLoading = ref(false)
const telegramLinkCode = ref('')
const telegramManualCommand = computed(() => telegramLinkCode.value ? `/link ${telegramLinkCode.value}` : '')
const currentTime = ref(new Date())
const showStatusAlert = ref(true)

// Lesson request modal
const showLessonRequestModal = ref(false)
const lessonRequestForm = ref({
  message: '',
  preferredDate: '',
  preferredTime: ''
})
const requestSubmitting = ref(false)

// Timer for updating countdown
let timeInterval = null
let alertTimer = null

// Computed properties
const user = computed(() => authStore.user)

const quickActions = computed(() => [
  ...(features.chat ? [{
    key: 'openChat',
    icon: 'mdi-chat',
    action: openChat
  }] : []),
  {
    key: 'editProfile',
    icon: 'mdi-account-edit',
    action: () => $router.push('/student/profile')
  },
  {
    key: 'contactAdmin',
    icon: 'mdi-phone',
    action: contactAdmin
  },
  {
    key: 'viewInstructors',
    icon: 'mdi-account-group',
    action: () => $router.push('/instructors')
  }
])

const learningStats = computed(() => ({
  totalLessons: 0, // Will be loaded from API
  completedHours: 0,
  nextMilestone: t('limitedStudent.dashboard.firstLesson')
}))

const timeUntilLesson = computed(() => {
  if (!nextLesson.value) return { display: '--:--', label: '' }

  try {
    // Деректерді тексеру
    if (!nextLesson.value.date || !nextLesson.value.timeSlot) {
      return { display: '⏰ Деректер жоқ', label: '' }
    }
    
    // Күн мен уақытты дұрыс біріктіру
    const lessonDateStr = nextLesson.value.date.split('T')[0] // 2025-11-02
    let lessonTimeStr = nextLesson.value.timeSlot
    
    // Егер timeSlot "09:00-10:00" форматында болса, тек басын алу
    if (lessonTimeStr.includes('-')) {
      lessonTimeStr = lessonTimeStr.split('-')[0]
    }
    
    // Жергілікті уақытты қолдану
    const lessonDateTime = new Date(`${lessonDateStr} ${lessonTimeStr}`)
    
    // Егер күн дұрыс емес болса
    if (isNaN(lessonDateTime.getTime())) {
      return { display: '⏰ Күн қатесі', label: '' }
    }
    
    // Қазіргі уақытты тексеру
    const now = currentTime.value || new Date()
    const diff = lessonDateTime - now
    
    if (diff <= 0) {
      return { 
        display: t('limitedStudent.dashboard.lessonStarted'), 
        label: '', 
        class: 'text-success' 
      }
    }

    // Сағат, минут, секунд есептеу
    const totalSeconds = Math.floor(diff / 1000)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    
    // 2 санмен көрсету (01, 02, ... 59)
    const formatTime = (num) => num.toString().padStart(2, '0')
    
    if (hours >= 24) {
      const days = Math.floor(hours / 24)
      const remainingHours = hours % 24
      return { 
        display: `${days}д ${formatTime(remainingHours)}:${formatTime(minutes)}`, 
        label: t('limitedStudent.dashboard.untilLesson') 
      }
    } else if (hours > 0) {
      return { 
        display: `${formatTime(hours)}:${formatTime(minutes)}:${formatTime(seconds)}`, 
        label: t('limitedStudent.dashboard.untilLesson') 
      }
    } else if (minutes > 0) {
      return { 
        display: `${formatTime(minutes)}:${formatTime(seconds)}`, 
        label: t('limitedStudent.dashboard.untilLesson') 
      }
    } else {
      return { 
        display: `00:${formatTime(seconds)}`, 
        label: t('limitedStudent.dashboard.untilLesson') 
      }
    }
  } catch (error) {
    return { display: '⏰ Есептеу қатесі', label: '' }
  }
})

const countdownClass = computed(() => {
  if (!nextLesson.value) return 'bg-grey-lighten-3'
  
  const lessonDate = new Date(nextLesson.value.date + ' ' + nextLesson.value.timeSlot.split('-')[0])
  const diff = lessonDate - currentTime.value
  const hours = Math.floor(diff / (1000 * 60 * 60))
  
  if (hours <= 2) return 'bg-error-lighten-4'
  if (hours <= 24) return 'bg-warning-lighten-4'
  return 'bg-primary-lighten-4'
})

// Methods
const loadInstructor = async () => {
  try {
    loading.value = true
    const response = await api.get('/student/instructor')
    instructor.value = response.data.instructor
  } catch (error) {
    // Error
  } finally {
    loading.value = false
  }
}

const loadNextLesson = async () => {
  try {
    const response = await api.get('/student/lessons/next')
    nextLesson.value = response.data.lesson
  } catch (error) {
    // Error
  }
}

const openChat = () => {
  if (instructor.value && instructor.value.userId) {
    // Create user object for chat from instructor data
    const instructorUser = {
      id: instructor.value.userId,
      fio: instructor.value.fio,
      role: 'INSTRUCTOR',
      phone: instructor.value.userPhone || instructor.value.phone,
      email: instructor.value.userEmail || instructor.value.email,
      photo: instructor.value.userPhoto || instructor.value.photo
    }
    chatStore.setCurrentChat(instructorUser)
    chatStore.showChatModal = true
  } else {
    snackbarStore.warning(t('limitedStudent.dashboard.noInstructorToChat'))
  }
}

const contactAdmin = () => {
  window.open('tel:+7-XXX-XXX-XX-XX')
}

const requestLesson = () => {
  if (instructor.value) {
    showLessonRequestModal.value = true
  } else {
    snackbarStore.warning(t('limitedStudent.dashboard.needInstructorFirst'))
  }
}

const openTelegramLink = async () => {
  telegramLinkLoading.value = true
  try {
    const response = await api.post('/student/telegram-link')
    telegramLinkCode.value = response.data?.code || ''
    if (response.data?.link) {
      window.open(response.data.link, '_blank', 'noopener,noreferrer')
      snackbarStore.success(t('student.dashboard.telegramOpened'))
    } else {
      throw new Error(t('student.dashboard.telegramLinkNotReceived'))
    }
  } catch (error) {
    snackbarStore.error(error.response?.data?.error || error.message || t('student.dashboard.telegramLinkError'))
  } finally {
    telegramLinkLoading.value = false
  }
}

const copyTelegramCode = async () => {
  if (!telegramManualCommand.value) return
  try {
    await navigator.clipboard.writeText(telegramManualCommand.value)
    snackbarStore.success(t('student.dashboard.telegramCodeCopied'))
  } catch (_) {
    snackbarStore.info(telegramManualCommand.value)
  }
}

const submitLessonRequest = async () => {
  if (!lessonRequestForm.value.message.trim()) {
    snackbarStore.warning(t('limitedStudent.lessonRequest.messageRequired'))
    return
  }

  requestSubmitting.value = true
  try {
    const response = await fetch('/api/student/request-lesson', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authStore.token}`
      },
      body: JSON.stringify(lessonRequestForm.value)
    })

    const data = await response.json()
    
    if (response.ok) {
      snackbarStore.success(t('limitedStudent.lessonRequest.requestSent', { instructor: data.notification.instructorName }))
      showLessonRequestModal.value = false
      // Reset form
      lessonRequestForm.value = {
        message: '',
        preferredDate: '',
        preferredTime: ''
      }
    } else {
      throw new Error(data.error || 'Request failed')
    }
  } catch (error) {
    // Error
    snackbarStore.error(error.message || t('limitedStudent.lessonRequest.requestError'))
  } finally {
    requestSubmitting.value = false
  }
}

const formatLessonDate = (dateStr) => {
  const date = new Date(dateStr)
  return date.toLocaleDateString('ru-RU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  })
}

// Helper functions for instructor availability
const getInstructorWorkingDays = () => {
  // Log removed
  // Log removed
  
  if (!instructor.value?.freeDays) return []
  
  // Check if it's already an array or a JSON string
  let freeDays = instructor.value.freeDays
  if (typeof freeDays === 'string') {
    try {
      freeDays = JSON.parse(freeDays)
    } catch (e) {
      // Error
      return []
    }
  }
  
  if (!Array.isArray(freeDays)) return []
  
  return freeDays.map(day => {
    // Handle different day formats (English/Kazakh/Russian)
    const dayMappings = {
      'monday': t('days.monday'),
      'tuesday': t('days.tuesday'),
      'wednesday': t('days.wednesday'),
      'thursday': t('days.thursday'),
      'friday': t('days.friday'),
      'saturday': t('days.saturday'),
      'sunday': t('days.sunday'),
      'дүйсенбі': t('days.monday'),
      'сейсенбі': t('days.tuesday'),
      'сәрсенбі': t('days.wednesday'),
      'бейсенбі': t('days.thursday'),
      'жұма': t('days.friday'),
      'сенбі': t('days.saturday'),
      'жексенбі': t('days.sunday')
    }
    const mapped = dayMappings[day.toLowerCase()] || day
    // Log removed
    return mapped
  })
}

const getInstructorWorkingTimes = () => {
  // Log removed
  
  if (!instructor.value?.freeTime) return []
  
  // Check if it's already an array or a JSON string
  let freeTimes = instructor.value.freeTime
  if (typeof freeTimes === 'string') {
    try {
      freeTimes = JSON.parse(freeTimes)
    } catch (e) {
      // Error
      return []
    }
  }
  
  if (!Array.isArray(freeTimes)) return []
  
  // Log removed
  
  // Return first 6 time slots to avoid overcrowding
  return freeTimes.slice(0, 6)
}

const updateCurrentTime = () => {
  currentTime.value = new Date()
}

// Lifecycle hooks
onMounted(async () => {
  await Promise.all([
    loadInstructor(),
    loadNextLesson()
  ])
  
  // Update time every second for real-time countdown
  timeInterval = setInterval(updateCurrentTime, 1000)
  
  // Auto-hide status alert after 10 seconds
  alertTimer = setTimeout(() => {
    showStatusAlert.value = false
  }, 10000)
})

onUnmounted(() => {
  if (timeInterval) {
    clearInterval(timeInterval)
  }
  if (alertTimer) {
    clearTimeout(alertTimer)
  }
})
</script>

<style scoped>
.limited-student-dashboard {
  min-height: calc(100vh - 64px);
}

.welcome-card {
  background: linear-gradient(135deg, rgba(33, 150, 243, 0.05) 0%, rgba(33, 150, 243, 0.02) 100%);
  border: 1px solid rgba(33, 150, 243, 0.1);
}

.telegram-connect-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.telegram-code-fallback {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(3, 169, 244, 0.22);
}

.telegram-code-row {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  padding: 4px 8px;
  border-radius: 6px;
  background: rgba(3, 169, 244, 0.12);
}

.telegram-code-row code {
  color: inherit;
  font-weight: 700;
}

.telegram-connect-content .v-btn {
  flex-shrink: 0;
}

.profile-avatar {
  border: 3px solid #2196F3;
}

.status-alert {
  border-left: 4px solid #FF9800 !important;
}

.instructor-info {
  border-radius: 12px;
  padding: 1.5rem;
  background: rgba(76, 175, 80, 0.05);
  border: 1px solid rgba(76, 175, 80, 0.2);
}

.feature-item {
  border-radius: 12px;
  transition: transform 0.2s ease;
  background: rgba(33, 150, 243, 0.05);
  border: 1px solid rgba(33, 150, 243, 0.1);
}

.feature-item:hover {
  transform: translateY(-2px);
  background: rgba(33, 150, 243, 0.1);
}

.countdown-display {
  border: 2px solid rgba(33, 150, 243, 0.3);
  background: rgba(33, 150, 243, 0.05);
}

.detail-row {
  display: flex;
  align-items: center;
  padding: 0.5rem;
  background: rgba(33, 150, 243, 0.05);
  border-radius: 8px;
  margin-bottom: 0.5rem;
}

.quick-action-item {
  border-radius: 8px !important;
  margin-bottom: 0.5rem !important;
  transition: all 0.2s ease !important;
}

.quick-action-item:hover {
  transform: translateX(4px);
}

.stat-item {
  background: rgba(76, 175, 80, 0.05);
  border: 1px solid rgba(76, 175, 80, 0.2);
  border-radius: 8px;
  padding: 1rem;
}

.contact-actions .v-btn {
  margin-bottom: 0.5rem;
}

.next-lesson-info {
  background: rgba(33, 150, 243, 0.05);
  border-radius: 12px;
  padding: 1rem;
}

.no-lesson {
  background: rgba(158, 158, 158, 0.05);
  border-radius: 12px;
  padding: 1rem;
}

/* Mobile responsiveness */
@media (max-width: 960px) {
  .limited-student-dashboard {
    background: #1a1a1a;
  }
  
  .v-container {
    padding: 2px !important;
  }
  
  .instructor-info {
    padding: 8px;
    margin-bottom: 8px;
  }
  
  .countdown-display h2 {
    font-size: 1.5rem !important;
  }

  .contact-actions {
    text-align: center;
  }
  
  .contact-actions .v-btn {
    width: 100%;
    margin-bottom: 4px;
  }
  
  .v-card {
    margin-bottom: 8px !important;
  }
  
  .v-card-title {
    padding: 8px !important;
    font-size: 0.85rem !important;
  }
  
  .v-card-text {
    padding: 8px !important;
  }
  
  .v-row {
    margin: 0 !important;
  }
  
  .v-col {
    padding: 2px !important;
  }
  
  .feature-item {
    padding: 8px !important;
  }
  
  .stat-item {
    padding: 6px !important;
    margin-bottom: 4px !important;
  }
}

@media (max-width: 600px) {
  .telegram-connect-content {
    align-items: stretch;
    flex-direction: column;
  }

  .v-container {
    padding: 1px !important;
  }
  
  .instructor-info {
    padding: 4px;
  }
  
  .countdown-display h2 {
    font-size: 1.2rem !important;
  }
  
  .v-card-title {
    padding: 4px 6px !important;
    font-size: 0.75rem !important;
  }
  
  .contact-actions .v-btn {
    font-size: 0.7rem !important;
    height: 28px !important;
  }
}
</style>
